"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { getTrackingConfig, logTrackingValidation, determineTrackingLocation } from "@/lib/tracking";

interface MixpanelScriptProps {
  mixpanelToken: string;
}

/** Keep Mixpanel off the early TBT window (same pattern as PageSense). */
const MIN_DELAY_MS = 5000;

type MixpanelClient = typeof import("mixpanel-browser").default;

export default function MixpanelScript({ mixpanelToken }: MixpanelScriptProps) {
  const pathname = usePathname();
  const mixpanelRef = useRef<MixpanelClient | null>(null);
  const readyRef = useRef(false);
  const listenersAttachedRef = useRef(false);

  // Defer loading mixpanel-browser until interaction or MIN_DELAY_MS
  useEffect(() => {
    if (!mixpanelToken) return;

    let cancelled = false;
    let timeoutId: number | undefined;

    const loadAndInit = async () => {
      if (cancelled || readyRef.current) return;
      readyRef.current = true;
      cleanup();

      try {
        const { default: mixpanel } = await import("mixpanel-browser");
        if (cancelled) return;

        const w = window as unknown as { __mixpanelInitialized?: boolean };
        if (!w.__mixpanelInitialized) {
          mixpanel.init(mixpanelToken, {
            stop_utm_persistence: true,
            loaded: () => {
              if (process.env.NODE_ENV === "development") {
                console.log("✅ Mixpanel initialized successfully");
              }
            },
          });
          w.__mixpanelInitialized = true;
        }

        mixpanelRef.current = mixpanel;

        // Initial page visit after load
        try {
          mixpanel.track("Page Visit", {
            "Page URL": window.location.href,
            "Page Path": window.location.pathname,
            "Page Title": document.title,
          });
        } catch (error) {
          console.error("❌ Failed to track page visit:", error);
        }

        setupEventTracking(mixpanel);
      } catch (error) {
        console.error("❌ Failed to initialize Mixpanel:", error);
        readyRef.current = false;
      }
    };

    const onInteraction = () => {
      void loadAndInit();
    };

    const cleanup = () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      window.removeEventListener("scroll", onInteraction);
      window.removeEventListener("pointerdown", onInteraction);
      window.removeEventListener("keydown", onInteraction);
      window.removeEventListener("touchstart", onInteraction);
    };

    window.addEventListener("scroll", onInteraction, { once: true, passive: true });
    window.addEventListener("pointerdown", onInteraction, { once: true });
    window.addEventListener("keydown", onInteraction, { once: true });
    window.addEventListener("touchstart", onInteraction, { once: true, passive: true });
    timeoutId = window.setTimeout(() => {
      void loadAndInit();
    }, MIN_DELAY_MS);

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [mixpanelToken]);

  // Track SPA navigations once Mixpanel is ready
  useEffect(() => {
    const mixpanel = mixpanelRef.current;
    if (!mixpanel || !readyRef.current) return;

    try {
      mixpanel.track("Page Visit", {
        "Page URL": window.location.href,
        "Page Path": pathname,
        "Page Title": document.title,
      });
    } catch (error) {
      console.error("❌ Failed to track page visit:", error);
    }
  }, [pathname]);

  const setupEventTracking = (mixpanel: MixpanelClient) => {
    if (typeof window === "undefined") return;

    const w = window as unknown as { __mixpanelListenersAttached?: boolean };
    if (w.__mixpanelListenersAttached || listenersAttachedRef.current) return;
    listenersAttachedRef.current = true;

    const deDupe = (() => {
      let lastKey = "";
      let lastTs = 0;
      return (key: string, windowMs = 800) => {
        const now = Date.now();
        if (key === lastKey && now - lastTs < windowMs) return true;
        lastKey = key;
        lastTs = now;
        return false;
      };
    })();

    const onClick = (evt: Event) => {
      try {
        const target = evt.target as HTMLElement;
        if (target && target.closest("[data-mixpanel-disabled]")) return;

        let el = target;
        while (el && el !== document.body) {
          const trackingId = el?.getAttribute("data-mixpanel-id");
          if (trackingId) {
            const config = getTrackingConfig(trackingId);
            if (config) {
              if ((config as { disabled?: boolean }).disabled) return;
              const href = el.getAttribute("href") || "";
              const text =
                (el.textContent || (el as HTMLElement).innerText || "").trim() || "Unknown";

              const dedupeKey = `cta|${trackingId}|${href}|${text}`;
              if (deDupe(dedupeKey)) return;

              const location = config.location || determineTrackingLocation(el, trackingId);

              try {
                mixpanel.track("CTA Click", {
                  "CTA Name": text,
                  "CTA Intent": config.intent,
                  "CTA URL": href,
                  "CTA Location": location,
                  "CTA Category": config.category,
                  "CTA Description": config.description,
                });
              } catch (error) {
                console.error("❌ Failed to track CTA click:", error);
              }
              return;
            }
          }
          el = el.parentNode as HTMLElement;
        }
      } catch (e) {
        console.error("CTA tracking error:", e);
      }
    };

    const onCustomEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;

      if (detail.type === "contact-us-submitted") {
        const data = detail.data;
        const dedupeKey = `contact|${data?.email || ""}|${data?.phone || ""}`;
        if (deDupe(dedupeKey)) return;

        mixpanel.track("Contact US", {
          "Position on page:": "Contact Page Form",
          "CTA Location": "Contact Form",
          email: data.email,
          phone: data.phone,
          name: data.name,
          company: data.company_name,
          description: data.description,
          screen_count: data.screen_count,
        });
      }

      if (detail.type === "book-demo-submitted") {
        const data = detail.data;
        const dedupeKey = `book-demo|${data?.email || ""}|${data?.cta_location || ""}`;
        if (deDupe(dedupeKey)) return;

        mixpanel.track("Book Demo Submitted", {
          "CTA Intent": "Book Demo Submitted",
          "CTA Location": data.cta_location,
          Name: data.name,
          Email: data.email,
          Phone: data.phone,
          "Screen Count": data.screen_count,
        });
      }

      if (detail.type === "digital-workplace-partner-form-start") {
        const dedupeKey = "dwp-partner-form-start";
        if (deDupe(dedupeKey, 2000)) return;

        mixpanel.track("Digital Workplace Partner Form Started", {
          "CTA Intent": "Digital Workplace Partner Form Started",
          "CTA Location": "Digital Workplace Partner Form",
        });
      }

      if (detail.type === "digital-workplace-partner-form-submit") {
        const data = detail.data;
        const dedupeKey = `dwp-partner-form|${data?.email || ""}`;
        if (deDupe(dedupeKey)) return;

        mixpanel.track("Digital Workplace Partner Form Submitted", {
          "CTA Intent": "Digital Workplace Partner Form Submitted",
          "CTA Location": "Digital Workplace Partner Form",
          email: data.email,
          name: data.name,
          company: data.company_name,
          phone: data.phone,
          job_title: data.job_title,
          country: data.country,
          role: data.role,
          practice_area: data.practice_area,
          help: data.help,
          environment: data.environment,
          message: data.message,
        });
      }

      if (detail.type === "hardware-quote-form-start") {
        const dedupeKey = "hardware-quote-form-start";
        if (deDupe(dedupeKey, 2000)) return;

        mixpanel.track("Hardware Quote Form Started", {
          "CTA Intent": "Hardware Quote Form Started",
          "CTA Location": "Hardware Quote Form",
        });
      }

      if (detail.type === "hardware-quote-form-submit") {
        const data = detail.data;
        const dedupeKey = `hardware-quote-form|${data?.email || ""}`;
        if (deDupe(dedupeKey)) return;

        mixpanel.track("Hardware Quote Form Submitted", {
          "CTA Intent": "Hardware Quote Form Submitted",
          "CTA Location": "Hardware Quote Form",
          email: data.email,
          name: data.name,
          company: data.company_name,
          phone: data.phone,
          company_size: data.company_size,
          number_of_locations: data.number_of_locations,
          number_of_screens: data.number_of_screens,
          primary_need: data.primary_need,
        });
      }
    };

    const onMessage = (e: MessageEvent) => {
      if (e.data.event && e.data.event.indexOf("calendly") === 0) {
        if (e.data.payload && e.data.payload.event && e.data.payload.invitee) {
          const eventUri = e.data.payload.event.uri;
          const inviteeUri = e.data.payload.invitee.uri;

          const dedupeKey = `calendly|${eventUri}|${inviteeUri}`;
          if (deDupe(dedupeKey)) return;

          mixpanel.track("Book Demo", {
            "Calendly Event URI": eventUri,
            "Calendly Invitee URI": inviteeUri,
            "Position on page": "Book Demo",
            "CTA Intent": "Book Demo Clicked",
            "CTA Name": "Book Demo",
            "CTA Location": "Calendly Integration",
          });
        }
      }
    };

    document.addEventListener("click", onClick, { capture: true, passive: true });
    // Custom events like 'mixpanelEvent' aren't in WindowEventMap
    window.addEventListener("mixpanelEvent", onCustomEvent as any);
    window.addEventListener("message", onMessage as any);

    w.__mixpanelListenersAttached = true;

    if (process.env.NODE_ENV === "development") {
      (window as unknown as { validateMixpanelTracking?: typeof logTrackingValidation }).validateMixpanelTracking =
        logTrackingValidation;
    }
  };

  return null;
}
